import { useState, type ReactElement } from 'react'
import { Check } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Card } from '../components/ui/card.js'
import { Badge } from '../components/ui/badge.js'
import { ScanResultRow } from '../components/ScanResultRow.js'
import { OverrideDialog } from '../components/OverrideDialog.js'
import { SavedPicturesStrip } from '../components/SavedPicturesStrip.js'
import { useStep } from '../state/useStep.js'
import { useDemoStore, type Material } from '../state/store.js'

const MATERIALS: Material[] = [
  'Laminate', 'Quartz', 'Granite', 'Marble', 'Butcher-block', 'Concrete', 'Tile', 'Solid-surface', 'Other',
]

export const CountertopResultsScreen = (): ReactElement => {
  const { goto } = useStep()
  const { state, dispatch } = useDemoStore()
  const top = state.countertop
  const [editing, setEditing] = useState<'material' | 'overhang' | null>(null)

  if (!top) return <AppShell title="Scan results"><p>No scan data yet.</p></AppShell>

  const overhangStatus: 'ok' | 'warn' = top.overhangInches >= 1 ? 'warn' : 'ok'

  return (
    <AppShell
      title="Scan results"
      showBack onBack={() => goto('scan-countertop')}
      progress={0.45}
      footer={<Button size="xl" onClick={() => goto('scan-undersink')}>Looks Good — Next: Under-Sink Scan</Button>}
    >
      <Badge variant="success" className="mb-4">
        <Check className="h-3 w-3" /> Scan complete
      </Badge>
      <h1 className="mb-2">Here's what we found</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Here's what we found from the countertop scan. We'll cover the rest in the next steps.
      </p>

      <SavedPicturesStrip scan="countertop" />

      <Card className="p-5">
        <ScanResultRow
          icon="🎨" label="Countertop material"
          value={top.material}
          status="ok"
          onOverride={() => setEditing('material')}
        />
        <ScanResultRow
          icon="📏" label="Countertop overhang"
          value={`${top.overhangInches} inches`}
          status={overhangStatus}
          onOverride={() => setEditing('overhang')}
        />
      </Card>
      <p className="text-xs text-muted-foreground text-center mt-3">Tap any row to edit its value</p>

      <OverrideDialog<Material>
        open={editing === 'material'}
        title="Countertop material"
        options={MATERIALS.map(m => ({ label: m, value: m }))}
        current={top.material}
        onSelect={v => { dispatch({ type: 'OVERRIDE_COUNTERTOP', patch: { material: v } }); setEditing(null) }}
        onClose={() => setEditing(null)}
      />
      <OverrideDialog<number>
        open={editing === 'overhang'}
        title="Countertop overhang (inches)"
        options={[
          { label: '0.5"', value: 0.5 }, { label: '1.0"', value: 1 },
          { label: '1.25"', value: 1.25 }, { label: '1.5"', value: 1.5 }, { label: '2.0"', value: 2 },
        ]}
        current={top.overhangInches}
        allowFreeform
        freeformPlaceholder="e.g. 1.75"
        onSelect={v => {
          const n = typeof v === 'number' ? v : Number(v)
          if (!Number.isFinite(n)) return
          dispatch({ type: 'OVERRIDE_COUNTERTOP', patch: { overhangInches: n } })
          setEditing(null)
        }}
        onClose={() => setEditing(null)}
      />
    </AppShell>
  )
}
